import { useEffect, useRef, useState, useCallback } from "react";
import { getForecast, getRadarFrames, getNowcastSummary } from "@/lib/api";

const FORECAST_REFRESH_MS = 5 * 60_000; // 5min — Open-Meteo updates every ~10min anyway, and this stays well within the free-tier daily quota
const RADAR_REFRESH_MS = 5 * 60_000; // 5min
const SUMMARY_MIN_INTERVAL_MS = 10 * 60_000; // 10min
const IDLE_PAUSE_MS = 5 * 60_000; // 5min — pause polling if user hasn't interacted (saves quota when tab is left open)

function classifySeverity(minutely_15, hourly) {
  const next4 = (minutely_15?.precipitation || []).slice(0, 16);
  const peak = next4.length ? Math.max(...next4) : 0;
  if (peak >= 7.5) return "severe";
  if (peak >= 2.5) return "heavy";
  if (peak >= 0.5) return "moderate";
  if (peak > 0) return "light";
  const probs = (hourly?.precipitation_probability || []).slice(0, 6);
  if (probs.length && Math.max(...probs) >= 60) return "light";
  return "clear";
}

// Unified-confidence label → severity. Mirrors backend mapping in
// generate_summary() so the frontend's "regenerate on severity change" trigger
// stays in lockstep with what the AI ultimately emits.
function labelToSeverity(label, minutely_15) {
  const next4 = (minutely_15?.precipitation || []).slice(0, 16);
  const peak = next4.length ? Math.max(...next4) : 0;
  const map = { none: "clear", minimal: "clear", low: "light", moderate: "moderate", high: "heavy" };
  let sev = map[label] || "clear";
  if (sev === "heavy" && peak >= 7.5) sev = "severe";
  else if (sev === "moderate" && peak >= 2.5) sev = "heavy";
  return sev;
}

/**
 * useLiveFeed — robust HTTP polling. Forecast every 60s, radar every 5min,
 * AI summary regenerated on severity change or every 10min. Auto-pauses when
 * the tab is hidden, refreshes immediately on focus.
 *
 * `confidence` (optional) — when provided, the unified Rain Confidence Engine
 * score/label/mode is forwarded to the AI summary endpoint so the narrative
 * is forced to agree with the Verdict ring.
 */
export default function useLiveFeed(location, confidence, targetLanguage) {
  const [status, setStatus] = useState("connecting");
  const [forecast, setForecast] = useState(null);
  const [radar, setRadar] = useState(null);
  const [summary, setSummary] = useState(null);
  const [lastSyncAt, setLastSyncAt] = useState(null);
  const [lastForecastAt, setLastForecastAt] = useState(null);
  const [lastRadarAt, setLastRadarAt] = useState(null);
  const [lastSummaryAt, setLastSummaryAt] = useState(null);
  const [quotaError, setQuotaError] = useState(null); // { code, message } when upstream is rate-limited

  const lastSeverityRef = useRef(null);
  const lastSummaryAtMs = useRef(0);
  const forecastTimerRef = useRef(null);
  const radarTimerRef = useRef(null);
  const cancelledRef = useRef(false);
  const locRef = useRef(location);
  locRef.current = location;
  const lastActivityRef = useRef(Date.now());
  // Mirror confidence into a ref so the polling closure always sees fresh
  // values without re-creating the timer.
  const confidenceRef = useRef(confidence);
  confidenceRef.current = confidence;
  // Phase 42 — Mirror language so the polling closure sees the latest
  // value, and force a summary regeneration when the user switches it.
  const langRef = useRef(targetLanguage);
  useEffect(() => {
    if (langRef.current !== targetLanguage) {
      langRef.current = targetLanguage;
      // Bust the severity-cache so the next forecast refresh fires a new
      // Gemini call in the new language without waiting 10min.
      lastSeverityRef.current = null;
      lastSummaryAtMs.current = 0;
    }
  }, [targetLanguage]);

  const refreshForecast = useCallback(async () => {
    const loc = locRef.current;
    if (!loc) return;
    try {
      const fc = await getForecast(loc.latitude, loc.longitude);
      if (cancelledRef.current) return;
      setForecast(fc);
      setQuotaError(null);
      const ts = Math.floor(Date.now() / 1000);
      setLastForecastAt(ts);
      setLastSyncAt(ts);
      setStatus("live");

      // Severity-aware summary regeneration. Severity is now derived from
      // the unified Rain Confidence Engine label (when available) so the AI
      // narrative matches the Verdict ring. Falls back to local classifier.
      const conf = confidenceRef.current;
      const sev = conf?.label
        ? labelToSeverity(conf.label, fc.minutely_15)
        : classifySeverity(fc.minutely_15, fc.hourly);
      const now = Date.now();
      const stale = now - lastSummaryAtMs.current >= SUMMARY_MIN_INTERVAL_MS;
      if (sev !== lastSeverityRef.current || stale) {
        lastSeverityRef.current = sev;
        lastSummaryAtMs.current = now;
        try {
          const s = await getNowcastSummary({
            location_name: [loc.name, loc.admin1].filter(Boolean).join(", "),
            current: fc.current || {},
            minutely_15: fc.minutely_15 || {},
            hourly: fc.hourly || {},
            // Unify Verdict ring + AI Intel narrative
            confidence_score: conf?.score ?? null,
            confidence_label: conf?.label ?? null,
            mode: conf?.mode ?? null,
            // Phase 42 — user's chosen language for AI summary
            target_language: langRef.current || null,
          });
          if (!cancelledRef.current) {
            setSummary(s);
            const ts2 = Math.floor(Date.now() / 1000);
            setLastSummaryAt(ts2);
            setLastSyncAt(ts2);
          }
        } catch (e) {
          console.warn("nowcast summary failed", e);
        }
      }
    } catch (e) {
      console.warn("forecast refresh failed", e);
      const code = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (code === 429 || code === 503) {
        setQuotaError({
          code,
          message:
            detail ||
            (code === 503
              ? "Upstream weather provider is rate-limited; retrying in ~60s."
              : "Upstream weather provider is rate-limited. Resets at 00:00 UTC."),
        });
      }
      setStatus("offline");
      // retry once after 5s on transient upstream blip (skip on 429/503 — the
      // upstream needs minutes-to-hours to recover; pounding it makes the
      // problem worse)
      if (code !== 429 && code !== 503) {
        setTimeout(() => {
          if (!cancelledRef.current && !document.hidden) refreshForecast();
        }, 5000);
      }
    }
  }, []);

  const refreshRadar = useCallback(async () => {
    try {
      const r = await getRadarFrames();
      if (cancelledRef.current) return;
      setRadar(r);
      const ts = Math.floor(Date.now() / 1000);
      setLastRadarAt(ts);
      setLastSyncAt(ts);
    } catch (e) {
      console.warn("radar refresh failed", e);
    }
  }, []);

  // Trigger an immediate refresh + restart timers whenever location changes
  useEffect(() => {
    if (!location) return;
    cancelledRef.current = false;
    // Clear stale data so we don't show the previous location's forecast
    // under a new location's name while the new fetch is in-flight (or
    // failing). Without this, a 502/429 on the new location leaves the
    // user staring at Hyderabad's data with the search bar reading "Mumbai".
    setForecast(null);
    setRadar(null);
    setSummary(null);
    lastSeverityRef.current = null;
    lastSummaryAtMs.current = 0;
    setStatus("connecting");

    refreshForecast();
    refreshRadar();

    clearInterval(forecastTimerRef.current);
    clearInterval(radarTimerRef.current);
    forecastTimerRef.current = setInterval(() => {
      if (document.hidden) return;
      // Idle-pause: if no user interaction in the last IDLE_PAUSE_MS, skip the
      // refresh. Polling resumes the moment the user touches mouse/keyboard/scroll.
      if (Date.now() - lastActivityRef.current > IDLE_PAUSE_MS) {
        setStatus("idle");
        return;
      }
      refreshForecast();
    }, FORECAST_REFRESH_MS);
    radarTimerRef.current = setInterval(() => {
      if (document.hidden) return;
      if (Date.now() - lastActivityRef.current > IDLE_PAUSE_MS) return;
      refreshRadar();
    }, RADAR_REFRESH_MS);

    return () => {
      clearInterval(forecastTimerRef.current);
      clearInterval(radarTimerRef.current);
    };
  }, [location?.latitude, location?.longitude, refreshForecast, refreshRadar]);

  // Pause/resume on tab visibility
  useEffect(() => {
    const onVis = () => {
      if (document.hidden) {
        setStatus("paused");
      } else {
        lastActivityRef.current = Date.now();
        setStatus("connecting");
        refreshForecast();
        refreshRadar();
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [refreshForecast, refreshRadar]);

  // Track user activity → exits idle-pause and resumes polling on first interaction
  useEffect(() => {
    let resumeScheduled = false;
    const bump = () => {
      const wasIdle = Date.now() - lastActivityRef.current > IDLE_PAUSE_MS;
      lastActivityRef.current = Date.now();
      if (wasIdle && !resumeScheduled && !document.hidden) {
        // We were idle-paused. Refresh immediately so the user sees fresh data.
        resumeScheduled = true;
        setStatus("connecting");
        Promise.resolve().then(() => {
          refreshForecast();
          refreshRadar();
          resumeScheduled = false;
        });
      }
    };
    const opts = { passive: true };
    window.addEventListener("mousemove", bump, opts);
    window.addEventListener("keydown", bump, opts);
    window.addEventListener("scroll", bump, opts);
    window.addEventListener("touchstart", bump, opts);
    window.addEventListener("click", bump, opts);
    return () => {
      window.removeEventListener("mousemove", bump);
      window.removeEventListener("keydown", bump);
      window.removeEventListener("scroll", bump);
      window.removeEventListener("touchstart", bump);
      window.removeEventListener("click", bump);
    };
  }, [refreshForecast, refreshRadar]);

  return {
    status,
    forecast,
    radar,
    summary,
    lastSyncAt,
    lastForecastAt,
    lastRadarAt,
    lastSummaryAt,
    quotaError,
    // Manual refresh — reused by the UI's refresh button. Refreshes both
    // forecast + radar in parallel and bumps the activity timer so polling
    // stays awake.
    refresh: useCallback(() => {
      lastActivityRef.current = Date.now();
      setStatus("connecting");
      return Promise.all([refreshForecast(), refreshRadar()]);
    }, [refreshForecast, refreshRadar]),
  };
}
